// The signed-in layout: sidebar · toolbar · content (web PLATFORM-SPEC §3).
import { Link, Outlet, useLocation, useNavigate, useRouterState } from "@tanstack/react-router";
import { CircleHelp, ExternalLink, LogOut, Menu, Search } from "lucide-react";
import { helpForScreen, helpHome, helpPage } from "./help";
import { useEffect, useState } from "react";
import { Button } from "react-aria-components";
import { onDeprecation, type DeprecationNotice } from "../api/client";
import { session } from "../api/session";
import { Banner } from "../components/Banner";
import { BrandLockup } from "../components/BrandLockup";
import { useCapabilities } from "./capabilities";
import { CommandPalette } from "./CommandPalette";
import { visibleNav } from "./nav";
import { screenForPath } from "./screens";
import { useSession } from "./useSession";

export function Shell() {
  const { data: caps, isError: capsFailed } = useCapabilities();
  const { testMode } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useRouterState({ select: (s) => s.status === "pending" });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [deprecation, setDeprecation] = useState<DeprecationNotice | null>(null);
  const online = useOnline();
  // The drawer (medium/compact layouts) belongs to the page it was opened on, so navigating closes it.
  const [drawerPath, setDrawerPath] = useState<string | null>(null);
  const drawerOpen = drawerPath === location.pathname;
  const setDrawerOpen = (open: boolean | ((was: boolean) => boolean)) =>
    setDrawerPath((current) => {
      const was = current === location.pathname;
      return (typeof open === "function" ? open(was) : open) ? location.pathname : null;
    });
  const screen = screenForPath(location.pathname);

  useEffect(() => onDeprecation(setDeprecation), []);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName));
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      } else if (!typing && e.key === "?") {
        window.open(screen ? helpForScreen(screen.id) : helpHome(), "_blank", "noopener");
      } else if (!typing && e.key === "[") {
        setDrawerPath((cur) => (cur === location.pathname ? null : location.pathname));
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, screen, location.pathname]);

  // Staff must have two-factor on before using administration screens.
  const staffNeedsMfa = caps?.isStaff && !caps.mfaEnabled;
  const onAdminRoute = /^\/(admin|ops|dev)(\/|$)/.test(location.pathname);

  return (
    <div className="shell" data-drawer={drawerOpen ? "open" : "closed"}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <aside className="sidebar" aria-label="Navigation">
        <div className="sidebar-head">
          <Link to="/" aria-label="Substratal home">
            <BrandLockup />
          </Link>
        </div>
        <Button className="search-button" onPress={() => setPaletteOpen(true)}>
          <Search size={16} aria-hidden /> Search <kbd>⌘K</kbd>
        </Button>
        <nav>
          {visibleNav(caps).map((section) => (
            <div key={section.id} className="nav-section">
              {section.label && <h2 className="nav-heading">{section.label}</h2>}
              <ul>
                {section.items.map((item) => (
                  <li key={item.to}>
                    {item.external ? (
                      <a href={item.to} target="_blank" rel="noopener" className="nav-item">
                        <item.icon size={18} aria-hidden />
                        <span>{item.label}</span>
                        <ExternalLink size={14} aria-label="(opens in a new tab)" className="nav-external" />
                      </a>
                    ) : (
                      <Link to={item.to} className="nav-item" activeOptions={{ exact: item.to === "/" || item.to === "/ops" }} activeProps={{ "aria-current": "page", className: "active" }}>
                        <item.icon size={18} aria-hidden />
                        <span>{item.label}</span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
      <div className="scrim" onClick={() => setDrawerOpen(false)} aria-hidden />
      <div className="main-column">
        <header className="toolbar">
          <Button className="icon-button drawer-toggle" aria-label="Open navigation" onPress={() => setDrawerOpen(true)}>
            <Menu size={20} aria-hidden />
          </Button>
          <div className="toolbar-title" aria-live="polite">
            {screen?.title ?? ""}
            {isLoading && <span className="spinner small" aria-label="Loading" />}
          </div>
          <div className="toolbar-actions">
            <a
              href={screen ? helpForScreen(screen.id) : helpHome()}
              target="_blank"
              rel="noopener"
              className="icon-button"
              aria-label={screen ? `Help for ${screen.title} (opens the help center)` : "Help center"}
              title="Help (?)"
            >
              <CircleHelp size={20} aria-hidden />
            </a>
            <span className="who" title={caps?.email}>
              {caps?.email}
            </span>
            <Button
              className="icon-button"
              aria-label="Sign out"
              onPress={async () => {
                await session.signOut();
                void navigate({ to: "/sign-in" });
              }}
            >
              <LogOut size={18} aria-hidden />
            </Button>
          </div>
        </header>
        {testMode && (
          <div className="test-banner" role="note">
            TEST — you're signed in to a test-mode account. Nothing here affects live data.
          </div>
        )}
        <div className="banners">
          {!online && <Banner tone="warning">You're offline. What's already loaded stays readable; changes are turned off until you're back online.</Banner>}
          {deprecation && (
            <Banner tone="warning">
              This version of the Hub uses API version {deprecation.version}, which is being retired{deprecation.sunset ? ` on ${new Date(deprecation.sunset).toLocaleDateString()}` : ""}. Reload to update.{" "}
              <a href={helpPage("developers/api-versioning.html")} target="_blank" rel="noopener">
                About API versions
              </a>
            </Banner>
          )}
          {capsFailed && <Banner tone="critical">Couldn't load your permissions. Some sections may be missing. Reload to try again.</Banner>}
          {staffNeedsMfa && (
            <Banner tone="warning" action={<Link to="/account/security/2fa/enable" className="button">Turn on two-factor</Link>}>
              Staff accounts need two-factor authentication. Administration, Operations and Developer screens are locked until you turn it on.
            </Banner>
          )}
        </div>
        <main id="main" tabIndex={-1}>
          {staffNeedsMfa && onAdminRoute ? (
            <div className="page">
              <h1>Two-factor authentication required</h1>
              <p>Turn on two-factor authentication to use this screen.</p>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

function useOnline() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}
